import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../../services/paymentService';
import { customerService } from '../../services/customerService';
import bankAccountService from '../../services/bankAccountService';
import { companyService } from '../../services/companyService';
import { formatCurrency } from '../../utils/formatters';
import { UnpaidInvoice, CreateCustomerPaymentData, PAYMENT_METHODS, InvoiceAllocation, Payment } from '../../types/payment';
import { TransactionReceipt } from './TransactionReceipt';

interface CustomerPaymentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  preSelectedCustomerId?: string;
}

// ── style tokens ─────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const sectionTitle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 18,
  fontWeight: 600,
  color: '#1c2033',
  margin: '0 0 16px',
  paddingBottom: 10,
  borderBottom: '1px solid rgba(92,78,51,0.15)',
};

const primaryBtn: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 28px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 28px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  background: '#faf7ee',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '11px 14px',
  borderBottom: '1px solid rgba(239,232,213,0.6)',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
};

export const CustomerPaymentForm: React.FC<CustomerPaymentFormProps> = ({
  onSuccess,
  onCancel,
  preSelectedCustomerId
}) => {
  const queryClient = useQueryClient();

  const [selectedCustomerId, setSelectedCustomerId] = useState(preSelectedCustomerId || '');
  const [paymentData, setPaymentData] = useState({
    totalAmount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    reference: '',
    notes: '',
    bankAccountId: ''
  });
  const [invoiceAllocations, setInvoiceAllocations] = useState<InvoiceAllocation[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [createdPayment, setCreatedPayment] = useState<Payment | null>(null);

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getCustomers({ limit: 100 })
  });

  const { data: bankAccountsData } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => bankAccountService.getBankAccounts({ limit: 100, isActive: true })
  });

  const { data: companyProfile } = useQuery({
    queryKey: ['company', 'profile'],
    queryFn: companyService.getProfile
  });

  const { data: unpaidInvoices, isLoading: loadingInvoices } = useQuery({
    queryKey: ['customer-unpaid-invoices', selectedCustomerId],
    queryFn: () => paymentService.getCustomerUnpaidInvoices(selectedCustomerId),
    enabled: !!selectedCustomerId
  });

  const createCustomerPaymentMutation = useMutation({
    mutationFn: paymentService.createCustomerPayment,
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (response.payments && response.payments.length > 0) {
        setCreatedPayment(response);
        setShowReceipt(true);
      }
    },
    onError: (error: any) => {
      console.error('Customer payment creation failed:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to process customer payment';
      setErrors([errorMessage]);
    }
  });

  useEffect(() => {
    setInvoiceAllocations([]);
    setPaymentData(prev => ({ ...prev, totalAmount: 0 }));
  }, [selectedCustomerId]);

  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);
  };

  const handlePaymentDataChange = (field: string, value: string | number) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: field === 'totalAmount' ? parseFloat(value.toString()) || 0 : value
    }));
    if (errors.length > 0) setErrors([]);
  };

  const handleAllocationChange = (invoiceId: string, amount: number) => {
    setInvoiceAllocations(prev => {
      const existing = prev.find(a => a.invoiceId === invoiceId);
      if (existing) {
        if (amount <= 0) return prev.filter(a => a.invoiceId !== invoiceId);
        return prev.map(a => a.invoiceId === invoiceId ? { ...a, amount } : a);
      } else if (amount > 0) {
        return [...prev, { invoiceId, amount }];
      }
      return prev;
    });
  };

  const handlePayFullInvoice = (invoice: UnpaidInvoice) => {
    handleAllocationChange(invoice.id, invoice.remainingAmount);
  };

  const getTotalAllocated = () => invoiceAllocations.reduce((sum, a) => sum + a.amount, 0);
  const getUnallocatedAmount = () => paymentData.totalAmount - getTotalAllocated();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors: string[] = [];

    if (!selectedCustomerId) validationErrors.push('Please select a customer');
    if (!paymentData.totalAmount || paymentData.totalAmount <= 0) validationErrors.push('Payment amount must be greater than 0');
    if (!paymentData.bankAccountId) validationErrors.push('Please select a bank account');
    if (getTotalAllocated() > paymentData.totalAmount) validationErrors.push('Total allocated amount cannot exceed payment amount');

    for (const allocation of invoiceAllocations) {
      const invoice = unpaidInvoices?.find(inv => inv.id === allocation.invoiceId);
      if (invoice && allocation.amount > invoice.remainingAmount) {
        validationErrors.push(`Allocation for invoice ${invoice.invoiceNumber} exceeds remaining balance`);
      }
    }

    if (validationErrors.length > 0) { setErrors(validationErrors); return; }
    setErrors([]);

    const customerPaymentData: CreateCustomerPaymentData = {
      customerId: selectedCustomerId,
      totalAmount: paymentData.totalAmount,
      paymentDate: paymentData.paymentDate,
      paymentMethod: paymentData.paymentMethod,
      reference: paymentData.reference,
      notes: paymentData.notes,
      bankAccountId: paymentData.bankAccountId,
      invoiceAllocations
    };

    createCustomerPaymentMutation.mutate(customerPaymentData);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('transaction-receipt');
    const originalContent = document.body.innerHTML;
    if (printContent) {
      document.body.innerHTML = printContent.outerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    }
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    onSuccess();
  };

  const customers = customersData?.data || [];
  const bankAccounts = bankAccountsData?.accounts || [];
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
  const selectedBankAccount = bankAccounts.find(b => b.id === paymentData.bankAccountId);

  if (showReceipt && createdPayment) {
    const companyAddress = [
      companyProfile?.address,
      companyProfile?.city,
      companyProfile?.state,
      companyProfile?.postalCode,
      companyProfile?.country
    ].filter(Boolean).join(' ');

    return (
      <TransactionReceipt
        payment={createdPayment}
        companyName={companyProfile?.name || 'Company Name'}
        companyAddress={companyAddress || 'Company Address'}
        companyPhone={companyProfile?.phone || ''}
        companyEmail={companyProfile?.email || ''}
        companyLogo={companyProfile?.logo || undefined}
        onClose={handleReceiptClose}
        onPrint={handlePrint}
      />
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 600, color: '#1c2033', margin: 0 }}>
          Customer Payment
        </h2>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c', margin: '6px 0 0' }}>
          Process payment and allocate to invoices
        </p>
      </div>

      {/* Error Banner */}
      {errors.length > 0 && (
        <div style={{ marginBottom: 20, padding: 16, background: 'rgba(192,80,77,0.08)', border: '1px solid rgba(192,80,77,0.3)', borderRadius: 6 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <svg style={{ width: 18, height: 18, color: '#c0504d', flexShrink: 0, marginTop: 1 }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#c0504d', margin: '0 0 8px' }}>
                Please fix the following errors:
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {errors.map((error, index) => (
                  <li key={index} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c0504d' }}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* Top Two-Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>

          {/* Customer Section */}
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24 }}>
            <h3 style={sectionTitle}>Customer & Payment Details</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Select Customer <span style={{ color: '#c0504d' }}>*</span></label>
                <select
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  style={inputStyle}
                  required
                >
                  <option value="">Select a customer...</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.email && `(${customer.email})`}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCustomer && (
                <div style={{ background: '#faf7ee', border: '1px solid rgba(92,78,51,0.22)', borderRadius: 6, padding: 16 }}>
                  <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#d97706', margin: '0 0 10px' }}>
                    Customer Information
                  </h4>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', fontWeight: 500 }}>{selectedCustomer.name}</div>
                  {selectedCustomer.email && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', marginTop: 4 }}>{selectedCustomer.email}</div>}
                  {selectedCustomer.phone && <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', marginTop: 2 }}>{selectedCustomer.phone}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Payment Details Section */}
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24 }}>
            <h3 style={sectionTitle}>Payment Information</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Amount */}
              <div>
                <label style={labelStyle}>Total Payment Amount <span style={{ color: '#c0504d' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>BD</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paymentData.totalAmount}
                    onChange={(e) => handlePaymentDataChange('totalAmount', e.target.value)}
                    style={{ ...inputStyle, paddingLeft: 32 }}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              {/* Date + Method */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Payment Date <span style={{ color: '#c0504d' }}>*</span></label>
                  <input
                    type="date"
                    value={paymentData.paymentDate}
                    onChange={(e) => handlePaymentDataChange('paymentDate', e.target.value)}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Payment Method <span style={{ color: '#c0504d' }}>*</span></label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => handlePaymentDataChange('paymentMethod', e.target.value)}
                    style={inputStyle}
                    required
                  >
                    {PAYMENT_METHODS.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bank Account */}
              <div>
                <label style={labelStyle}>Deposit to Bank Account <span style={{ color: '#c0504d' }}>*</span></label>
                <select
                  value={paymentData.bankAccountId}
                  onChange={(e) => handlePaymentDataChange('bankAccountId', e.target.value)}
                  style={inputStyle}
                  required
                >
                  <option value="">Select bank account...</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountName} - {account.bankName} (...{account.accountNumber.slice(-4)})
                    </option>
                  ))}
                </select>
                {selectedBankAccount && (
                  <div style={{ marginTop: 8, background: 'rgba(100,200,100,0.06)', border: '1px solid rgba(100,200,100,0.2)', borderRadius: 4, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6fc96f', fontWeight: 500 }}>{selectedBankAccount.accountName}</span>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6fc96f' }}>Balance: {formatCurrency(selectedBankAccount.balance || 0)}</span>
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#4a9a4a', marginTop: 3 }}>
                      {selectedBankAccount.bankName} • ****{selectedBankAccount.accountNumber.slice(-4)}
                    </div>
                  </div>
                )}
              </div>

              {/* Reference */}
              <div>
                <label style={labelStyle}>Reference Number</label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) => handlePaymentDataChange('reference', e.target.value)}
                  placeholder="e.g., Check #1234, Transaction ID"
                  style={inputStyle}
                />
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea
                  rows={3}
                  value={paymentData.notes}
                  onChange={(e) => handlePaymentDataChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Allocation Section */}
        {selectedCustomerId && (
          <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 600, color: '#1c2033', margin: 0 }}>
                Invoice Allocation
              </h3>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', display: 'flex', gap: 16 }}>
                <span>Total: <span style={{ color: '#1c2033', fontWeight: 600 }}>{formatCurrency(paymentData.totalAmount)}</span></span>
                <span>Allocated: <span style={{ color: '#6fc96f', fontWeight: 600 }}>{formatCurrency(getTotalAllocated())}</span></span>
                <span>Remaining: <span style={{ color: getUnallocatedAmount() >= 0 ? '#d97706' : '#c0504d', fontWeight: 600 }}>{formatCurrency(getUnallocatedAmount())}</span></span>
              </div>
            </div>

            {loadingInvoices ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>
                  <svg style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading unpaid invoices...
                </div>
              </div>
            ) : unpaidInvoices && unpaidInvoices.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Invoice #</th>
                      <th style={thStyle}>Issue Date</th>
                      <th style={thStyle}>Due Date</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Allocate</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaidInvoices.map((invoice) => {
                      const currentAllocation = invoiceAllocations.find(a => a.invoiceId === invoice.id);
                      const isOverdue = new Date(invoice.dueDate) < new Date();

                      return (
                        <tr
                          key={invoice.id}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={{ ...tdStyle, color: '#d97706', fontWeight: 500 }}>{invoice.invoiceNumber}</td>
                          <td style={tdStyle}>{new Date(invoice.issueDate).toLocaleDateString()}</td>
                          <td style={tdStyle}>
                            <span style={{ color: isOverdue ? '#c0504d' : '#5b5a52', fontWeight: isOverdue ? 600 : 400 }}>
                              {new Date(invoice.dueDate).toLocaleDateString()}
                              {isOverdue && <span style={{ marginLeft: 6, fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.1em' }}>(OVERDUE)</span>}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(invoice.total)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#c0504d', fontWeight: 600 }}>{formatCurrency(invoice.remainingAmount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>BD</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={invoice.remainingAmount}
                                value={currentAllocation?.amount || ''}
                                onChange={(e) => handleAllocationChange(invoice.id, parseFloat(e.target.value) || 0)}
                                style={{ ...inputStyle, width: 90, paddingLeft: 24, padding: '6px 8px 6px 24px' }}
                                placeholder="0.00"
                              />
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handlePayFullInvoice(invoice)}
                              style={{
                                background: 'rgba(92,78,51,0.2)',
                                border: '1px solid rgba(92,78,51,0.35)',
                                borderRadius: 4,
                                padding: '4px 12px',
                                fontFamily: "'Inter', sans-serif",
                                fontSize: 9,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: '#d97706',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(201,165,90,0.18)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(92,78,51,0.2)')}
                            >
                              Pay Full
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : selectedCustomerId ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <svg style={{ width: 48, height: 48, color: '#6b6a5c', margin: '0 auto 12px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, color: '#1c2033', margin: '0 0 6px' }}>No unpaid invoices</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>This customer has no outstanding invoices.</p>
              </div>
            ) : null}

            {/* Allocation Summary */}
            {getTotalAllocated() > 0 && (
              <div style={{ marginTop: 16, background: 'rgba(100,200,100,0.06)', border: '1px solid rgba(100,200,100,0.2)', borderRadius: 6, padding: 16 }}>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6fc96f', margin: '0 0 12px' }}>
                  Payment Allocation Summary
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#4a9a4a' }}>
                    <span>Total Payment Amount:</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(paymentData.totalAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#4a9a4a' }}>
                    <span>Allocated to Invoices:</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(getTotalAllocated())}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#4a9a4a', paddingTop: 6, borderTop: '1px solid rgba(100,200,100,0.2)' }}>
                    <span>Customer Credit (Unallocated):</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(getUnallocatedAmount())}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
          <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button
            type="submit"
            disabled={createCustomerPaymentMutation.isPending || !selectedCustomerId || paymentData.totalAmount <= 0 || !paymentData.bankAccountId}
            style={{
              ...primaryBtn,
              opacity: (createCustomerPaymentMutation.isPending || !selectedCustomerId || paymentData.totalAmount <= 0 || !paymentData.bankAccountId) ? 0.5 : 1,
              cursor: (createCustomerPaymentMutation.isPending || !selectedCustomerId || paymentData.totalAmount <= 0 || !paymentData.bankAccountId) ? 'not-allowed' : 'pointer'
            }}
          >
            {createCustomerPaymentMutation.isPending ? 'Processing Payment...' : 'Process Payment'}
          </button>
        </div>
      </form>
    </div>
  );
};
